<template lang="pug">
b-card
  validation-observer(ref="form", tag="form")
    b-form
      b-row
        // Name
        b-col(md="6")
          b-form-group(
            :label="$t('label.Name')",
            :label-for="$t('label.name')",
            description=""
          )
            validation-provider(:name="$t('label.name')", rules="required", v-slot="{ errors }")
              b-form-input(
                id="name",
                v-model="data.name",
                trim,
                :placeholder="$t('placeholder.name')"
                :disabled="readOnly"
              )
              small(class="text-danger") {{ errors[0] }}

        // Value
        b-col(md="6")
          b-form-group(
            :label="$t('label.Value')",
            :label-for="$t('label.value')",
            description=""
          )
            validation-provider(:name="$t('label.value')", rules="required", v-slot="{ errors }")
              v-select(
                v-model="data.value_id",
                :reduce="(values) => values.value",
                label="title",
                :options="values",
                :placeholder="$t('label.Value')"
                :disabled="readOnly"
              )
              small(class="text-danger") {{ errors[0] }}

        // Discussion question
        b-col(md="6")
          b-form-group(
            :label="$t('label.discussion_question')",
            :label-for="$t('label.discussion_question')",
            description=""
          )
            b-form-textarea(
              id="description",
              v-model="data.description",
              rows="3",
              :placeholder="$t('label.discussion_question')"
              :disabled="readOnly"
            )

        // Image
        b-col(md="6")
          b-form-group(label-for="v-img")
            label {{ $t('label.img') }}

            // Loading: wait before showing image state
            div(v-if="isSubmitting")
              b-spinner(small)

            // Has image (existing or newly selected) and not deleted
            div(v-else-if="(data.imageKey || imgData) && !deleteImage")
              // Thumbnail: new image takes priority over existing
              img(
                v-if="imgData",
                :src="imgData.base64",
                style="max-height: 100px; cursor: pointer; border-radius: 4px; display: block; margin-bottom: 8px;",
                @click="openNewImage()"
              )
              img(
                v-else-if="data.imgSrc",
                :src="data.imgSrc",
                style="max-height: 100px; cursor: pointer; border-radius: 4px; display: block; margin-bottom: 8px;",
                @click="openImage(data.imageKey)"
              )
              feather-icon(
                v-else,
                icon="ImageIcon",
                size="40",
                style="display: block; margin-bottom: 8px; opacity: 0.4;",
              )
              div(v-if="!readOnly" class="d-flex mt-1")
                b-button(size="sm", variant="outline-secondary", @click="uploadImage()") {{ $t('message.modify') }}
                b-button(size="sm", variant="outline-danger", class="ml-1", @click="clearImage()") {{ $t('message.delete') }}

            // No image or deleted
            div(v-else)
              b-button(v-if="!readOnly" variant="primary", @click="uploadImage()") {{ $t('message.upload') }}
              span(v-else class="text-muted") -

            image-compressor(v-if="!readOnly" ref="compressor", style="display: none", :done="getImg")

      // Button
      b-row(v-if="!readOnly")
        b-col(
          cols="12"
          class="text-right"
        )
          b-button(
            variant="primary",
            :disabled="isSubmitting",
            @click="validateAndSubmit"
          )
            | {{ $t('message.submit') }}
</template>

<script>
// eslint-disable-next-line import/extensions
import { ref, toRefs, onMounted } from '@vue/composition-api/dist/vue-composition-api'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import { required } from "@validations";
import useNotifications from '@/composables/useNotifications'
import i18n from '@/libs/i18n'
import useCommon from '@/views/organization/useCommon'
import vSelect from 'vue-select'
import ImageCompressor from '@/views/habit/ImageCompressor.vue'
import { BSpinner } from 'bootstrap-vue'

export default {
  name: 'Form',

  components: {
    ValidationObserver,
    ValidationProvider,
    vSelect,
    ImageCompressor,
    BSpinner,
  },

  props: ['data', 'isSubmitting', 'readOnly'],

  setup(props, { emit }) {
    const { showErrorMessage } = useNotifications()
    const form = ref(null)
    const compressor = ref(null)
    const imgData = ref(null)
    const deleteImage = ref(false)
    const { getValuesForDropDown, values, openImage } = useCommon()

    const uploadImage = () => {
      compressor.value.$el.click()
    }

    const getImg = (obj) => {
      imgData.value = obj.compressed
      deleteImage.value = false
    }

    const clearImage = () => {
      imgData.value = null
      deleteImage.value = true
    }

    const openNewImage = () => {
      if (imgData.value?.blob) window.open(imgData.value.blob)
    }

    const validateBehaviour = () => new Promise((resolve, reject) => {
      form.value.validate().then(success => {
        if (success) resolve(true)
        else reject()
      })
    })

    const validateAndSubmit = () => {
      validateBehaviour()
      .then(() => emit('submit', toRefs(props.data), imgData.value, deleteImage.value))
      .catch(() => showErrorMessage(i18n.t('message.requiredFieldsIncomplete')))
    }

    onMounted(() => {
      getValuesForDropDown()
    })

    return {
      required,
      form,
      compressor,
      imgData,
      deleteImage,
      uploadImage,
      getImg,
      clearImage,
      openNewImage,
      validateAndSubmit,
      values,
      openImage,
    }
  },
}
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
